import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../App";
import {
  Users, ClipboardList, Trophy, Download, LogOut, Search, Filter,
  ChevronLeft, ChevronRight, RefreshCw, Loader2, BarChart3, Globe,
  MessageSquare, Gift, Shield, Activity, UserCheck, TrendingUp
} from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [registrations, setRegistrations] = useState({ registrations: [], total: 0, page: 1, pages: 1 });
  const [surveys, setSurveys] = useState({ sessions: [], total: 0, page: 1, pages: 1 });
  const [surveyDetail, setSurveyDetail] = useState({ rows: [], total: 0, page: 1, pages: 1 });
  const [openResponses, setOpenResponses] = useState({ responses: [], total: 0, page: 1, pages: 1 });
  const [prizeEntries, setPrizeEntries] = useState({ entries: [], total: 0, page: 1, pages: 1 });
  const [forumUsers, setForumUsers] = useState({ users: [], total: 0, page: 1, pages: 1 });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("overview");

  const apiFetch = useCallback(async (url) => {
    const r = await fetch(`${API_URL}${url}`, { credentials: "include" });
    if (!r.ok) throw new Error("Failed");
    return r.json();
  }, []);

  const fetchStats = useCallback(async () => {
    try { setStats(await apiFetch("/api/admin/stats")); }
    catch { toast.error("Failed to load stats"); }
    finally { setLoading(false); }
  }, [apiFetch]);
  const fetchRegistrations = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "20" });
      if (searchQuery) params.append("search", searchQuery);
      setRegistrations(await apiFetch(`/api/admin/registrations?${params}`));
    } catch {
      toast.error("Failed to load registrations");
    }
  }, [apiFetch, page, searchQuery]);
  const fetchSurveys = useCallback(async () => {
    try { setSurveys(await apiFetch(`/api/admin/surveys?page=${page}&limit=20`)); } catch { toast.error("Failed to load surveys"); }
  }, [apiFetch, page]);
  const fetchSurveyDetail = useCallback(async () => {
    try { setSurveyDetail(await apiFetch(`/api/admin/survey-detail?page=${page}&limit=20`)); } catch { toast.error("Failed to load survey data"); }
  }, [apiFetch, page]);
  const fetchOpenResponses = useCallback(async () => {
    try { setOpenResponses(await apiFetch(`/api/admin/open-responses?page=${page}&limit=20`)); } catch { toast.error("Failed to load responses"); }
  }, [apiFetch, page]);
  const fetchPrizeEntries = useCallback(async () => {
    try { setPrizeEntries(await apiFetch(`/api/admin/prize-entries?page=${page}&limit=20`)); } catch { toast.error("Failed to load prize entries"); }
  }, [apiFetch, page]);
  const fetchForumUsers = useCallback(async () => {
    try { setForumUsers(await apiFetch(`/api/admin/forum-users?page=${page}&limit=20`)); } catch { toast.error("Failed to load forum users"); }
  }, [apiFetch, page]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    const fetchers = {
      registrations: fetchRegistrations,
      surveys: fetchSurveys,
      "survey-data": fetchSurveyDetail,
      responses: fetchOpenResponses,
      prize: fetchPrizeEntries,
      forum: fetchForumUsers,
    };
    if (fetchers[activeTab]) fetchers[activeTab]();
  }, [activeTab, fetchRegistrations, fetchSurveys, fetchSurveyDetail, fetchOpenResponses, fetchPrizeEntries, fetchForumUsers]);

  const handleExport = async (type) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/export/${type}`, { credentials: "include" });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${type}.csv`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); a.remove();
      toast.success(`${type} exported`);
    } catch { toast.error("Export failed"); }
  };

  const handleSelectWinners = async () => {
    if (!confirm("Select 100 random winners? This cannot be undone.")) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/select-winners?count=100`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error("Failed");
      const d = await r.json();
      toast.success(`${d.selected} winners selected!`);
      fetchStats(); fetchPrizeEntries();
    } catch { toast.error("Failed"); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-12 h-12 text-[#FBBF24] animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
              <span className="text-white font-bold">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-sm hidden sm:block">{user?.email}</span>
              <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="logout-button">
                <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); }}>
          <div className="overflow-x-auto mb-8">
            <TabsList className="bg-[#141414] border border-white/10 p-1 inline-flex min-w-max">
              <TabsTrigger value="overview" className="data-[state=active]:bg-[#FBBF24] data-[state=active]:text-black" data-testid="tab-overview"><BarChart3 className="w-4 h-4 mr-1.5" />Overview</TabsTrigger>
              <TabsTrigger value="survey-data" className="data-[state=active]:bg-[#FBBF24] data-[state=active]:text-black" data-testid="tab-survey-data"><ClipboardList className="w-4 h-4 mr-1.5" />Survey Data</TabsTrigger>
              <TabsTrigger value="responses" className="data-[state=active]:bg-[#FBBF24] data-[state=active]:text-black" data-testid="tab-responses"><MessageSquare className="w-4 h-4 mr-1.5" />Fan Voices</TabsTrigger>
              <TabsTrigger value="forum" className="data-[state=active]:bg-[#FBBF24] data-[state=active]:text-black" data-testid="tab-forum"><Shield className="w-4 h-4 mr-1.5" />Forum Users</TabsTrigger>
              <TabsTrigger value="registrations" className="data-[state=active]:bg-[#FBBF24] data-[state=active]:text-black" data-testid="tab-registrations"><Users className="w-4 h-4 mr-1.5" />Registrations</TabsTrigger>
              <TabsTrigger value="prize" className="data-[state=active]:bg-[#FBBF24] data-[state=active]:text-black" data-testid="tab-prize"><Trophy className="w-4 h-4 mr-1.5" />Prize Draw</TabsTrigger>
            </TabsList>
          </div>

          {/* ====== OVERVIEW ====== */}
          <TabsContent value="overview">
            <div className="space-y-8">
              {/* Top stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                <StatCard icon={ClipboardList} label="Surveys Started" value={stats?.total_survey_starts || 0} color="purple" />
                <StatCard icon={ClipboardList} label="Completed" value={stats?.total_survey_completions || 0} subtext={`${stats?.completion_rate || 0}%`} color="green" />
                <StatCard icon={Users} label="Registrations" value={stats?.total_registrations || 0} color="blue" />
                <StatCard icon={UserCheck} label="Verified" value={stats?.total_verified || 0} color="green" />
                <StatCard icon={Shield} label="Fan Passes" value={stats?.total_fan_passes || 0} color="yellow" />
                <StatCard icon={Activity} label="Active Sessions" value={stats?.active_sessions || 0} color="green" />
                <StatCard icon={MessageSquare} label="Forum Comments" value={stats?.total_forum_comments || 0} color="blue" />
                <StatCard icon={Trophy} label="Prize Eligible" value={stats?.total_prize_eligible || 0} subtext={`${stats?.total_winners || 0} won`} color="yellow" />
              </div>

              {/* Completion rate */}
              <div className="bg-[#141414] border border-white/10 p-6">
                <h3 className="text-white font-bold mb-4">Survey Completion Rate</h3>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">{stats?.total_survey_completions || 0} of {stats?.total_survey_starts || 0} completed</span>
                  <span className="text-[#FBBF24] font-bold">{stats?.completion_rate || 0}%</span>
                </div>
                <Progress value={stats?.completion_rate || 0} className="h-3" />
              </div>

              {/* Registration Trend */}
              {stats?.registration_trend?.length > 0 && (
                <div className="bg-[#141414] border border-white/10 p-6">
                  <h3 className="text-white font-bold mb-6 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#FBBF24]" />Registration Trend</h3>
                  <div className="h-48 flex items-end gap-1">
                    {(() => {
                      const trendMax = Math.max(...stats.registration_trend.map(d => d.count));
                      return stats.registration_trend.map((day, i) => {
                        const h = trendMax > 0 ? (day.count / trendMax) * 100 : 0;
                        return (
                          <div key={day.date} className="flex-1 h-full flex flex-col items-center justify-end gap-1 group relative">
                            <div className="absolute -top-8 bg-black/90 border border-white/20 px-2 py-1 text-xs text-white rounded hidden group-hover:block whitespace-nowrap z-10">{day.date}: {day.count}</div>
                            <div className="w-full bg-[#FBBF24] hover:bg-[#F59E0B] transition-colors rounded-t-sm cursor-pointer min-h-[2px]" style={{ height: `${Math.max(h, 2)}%` }} />
                            {i % Math.max(1, Math.floor(stats.registration_trend.length / 5)) === 0 && <span className="text-gray-600 text-[9px] truncate w-full text-center">{day.date.slice(5)}</span>}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* Charts Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                <BarChartCard title="Top Countries" icon={Globe} data={stats?.top_countries?.slice(0, 8)} labelKey="country" color="#FBBF24" />
                <BarChartCard title="Top Clubs" icon={Trophy} data={stats?.top_clubs?.slice(0, 8)} labelKey="club" color="#10B981" />
                <BarChartCard title="Age Distribution" icon={Users} data={stats?.age_groups} labelKey="group" color="#3B82F6" />
                <BarChartCard title="Follow Frequency" icon={Activity} data={stats?.follow_frequency} labelKey="freq" color="#8B5CF6" />
                <BarChartCard title="Support Duration" icon={ClipboardList} data={stats?.support_duration} labelKey="duration" color="#F97316" />
                <BarChartCard title="Connection Level" icon={TrendingUp} data={stats?.connection_levels} labelKey="level" color="#06B6D4" />
                <PieChartCard title="Do You Feel Recognised?" data={stats?.recognition} labelKey="response" />
                <PieChartCard title="Do Clubs Do Enough?" data={stats?.clubs_enough} labelKey="response" />
                <BarChartCard title="Engagement Methods" icon={Activity} data={stats?.engagement_methods} labelKey="method" color="#EC4899" />
                <PieChartCard title="Membership Interest" data={stats?.membership_interest} labelKey="response" />
              </div>

              {/* Engagement Barriers */}
              {stats?.barriers?.length > 0 && (
                <div className="bg-[#141414] border border-white/10 p-6">
                  <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Filter className="w-5 h-5 text-red-400" />Top Engagement Barriers</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {stats.barriers.map((b, i) => {
                      const max = stats.barriers[0]?.count || 1;
                      return (
                        <div key={b.barrier}>
                          <div className="flex items-center justify-between mb-1"><span className="text-white text-sm">{b.barrier}</span><span className="text-red-400 font-bold text-sm">{b.count}</span></div>
                          <div className="h-2 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-red-400 rounded-full" style={{ width: `${(b.count / max) * 100}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Most Active Forum Topics */}
              {stats?.active_topics?.length > 0 && (
                <div className="bg-[#141414] border border-white/10 p-6">
                  <h3 className="text-white font-bold mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#FBBF24]" />Most Active Forum Topics</h3>
                  <div className="space-y-2">
                    {stats.active_topics.map((t) => (
                      <div key={t.topic_id} className="flex items-center justify-between p-3 bg-white/5 border border-white/5">
                        <div><span className="text-[#FBBF24] text-xs uppercase tracking-wider">{t.category_id}</span><p className="text-white text-sm mt-1">{t.title}</p></div>
                        <span className="text-[#FBBF24] font-bold">{t.comment_count} <span className="text-gray-500 text-xs font-normal">replies</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Export Buttons */}
              <div className="flex flex-wrap gap-4">
                {["registrations", "surveys", "winners"].map((t) => (
                  <button key={t} onClick={() => handleExport(t)} className="flex items-center gap-2 bg-white/10 border border-white/20 text-white px-4 py-2 hover:bg-white/20 transition-colors" data-testid={`export-${t}-button`}>
                    <Download className="w-4 h-4" />Export {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ====== SURVEY DATA ====== */}
          <TabsContent value="survey-data">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-white font-bold text-lg">Survey Responses (Completed)</h2>
                <button onClick={() => { setPage(1); fetchSurveyDetail(); }} className="flex items-center gap-2 bg-[#FBBF24] text-black font-bold px-4 py-2 hover:bg-[#F59E0B] transition-colors"><RefreshCw className="w-4 h-4" />Refresh</button>
              </div>
              <div className="bg-[#141414] border border-white/10 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-gray-400 whitespace-nowrap">Date</TableHead>
                      <TableHead className="text-gray-400">Country</TableHead>
                      <TableHead className="text-gray-400">Age</TableHead>
                      <TableHead className="text-gray-400">Club</TableHead>
                      <TableHead className="text-gray-400 whitespace-nowrap">How Long</TableHead>
                      <TableHead className="text-gray-400 whitespace-nowrap">Follow Freq</TableHead>
                      <TableHead className="text-gray-400">Recognised?</TableHead>
                      <TableHead className="text-gray-400 whitespace-nowrap">Clubs Enough?</TableHead>
                      <TableHead className="text-gray-400">Connection</TableHead>
                      <TableHead className="text-gray-400 whitespace-nowrap">Membership?</TableHead>
                      <TableHead className="text-gray-400">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {surveyDetail.rows.map((row) => (
                      <TableRow key={row.session_id} className="border-white/10">
                        <TableCell className="text-gray-500 text-xs whitespace-nowrap">{row.completed_at ? new Date(row.completed_at).toLocaleDateString() : "-"}</TableCell>
                        <TableCell className="text-white text-sm">{row.country || "-"}</TableCell>
                        <TableCell className="text-gray-300 text-sm">{row.age_group || "-"}</TableCell>
                        <TableCell className="text-[#FBBF24] text-sm font-medium">{row.club_supported || "-"}</TableCell>
                        <TableCell className="text-gray-300 text-sm">{row.support_duration || "-"}</TableCell>
                        <TableCell className="text-gray-300 text-sm">{row.follow_frequency || "-"}</TableCell>
                        <TableCell><Badge value={row.feel_recognised} /></TableCell>
                        <TableCell><Badge value={row.clubs_do_enough} /></TableCell>
                        <TableCell className="text-gray-300 text-sm">{row.connection_level || "-"}</TableCell>
                        <TableCell><Badge value={row.membership_interested} /></TableCell>
                        <TableCell className="text-gray-300 text-sm">{row.monthly_price || "-"}</TableCell>
                      </TableRow>
                    ))}
                    {surveyDetail.rows.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-gray-500 py-8">No completed surveys</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <Pagination page={surveyDetail.page} pages={surveyDetail.pages} total={surveyDetail.total} onPageChange={setPage} />
            </div>
          </TabsContent>

          {/* ====== FAN VOICES ====== */}
          <TabsContent value="responses">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-white font-bold text-lg">Fan Voices — Open Responses</h2>
                <button onClick={() => { setPage(1); fetchOpenResponses(); }} className="flex items-center gap-2 bg-[#FBBF24] text-black font-bold px-4 py-2 hover:bg-[#F59E0B] transition-colors"><RefreshCw className="w-4 h-4" />Refresh</button>
              </div>
              <div className="space-y-4">
                {openResponses.responses.map((r) => (
                  <div key={r.respondent_id + r.saved_at} className="bg-[#141414] border border-white/10 p-6">
                    <div className="flex items-center gap-4 mb-3 text-sm">
                      <span className="text-gray-500 font-mono">{r.respondent_id}</span>
                      <span className="text-gray-600">|</span>
                      <span className="text-gray-500">{new Date(r.saved_at).toLocaleString()}</span>
                    </div>
                    <p className="text-white leading-relaxed">{r.response}</p>
                  </div>
                ))}
                {openResponses.responses.length === 0 && <div className="bg-[#141414] border border-white/10 p-8 text-center"><MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" /><p className="text-gray-500">No open responses yet</p></div>}
              </div>
              <Pagination page={openResponses.page} pages={openResponses.pages} total={openResponses.total} onPageChange={setPage} />
            </div>
          </TabsContent>

          {/* ====== FORUM USERS ====== */}
          <TabsContent value="forum">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-white font-bold text-lg">Forum Users (Fan Pass Holders)</h2>
                <button onClick={() => { setPage(1); fetchForumUsers(); }} className="flex items-center gap-2 bg-[#FBBF24] text-black font-bold px-4 py-2 hover:bg-[#F59E0B] transition-colors"><RefreshCw className="w-4 h-4" />Refresh</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-[#141414] border border-white/10 p-4"><p className="text-gray-400 text-xs mb-1">Total Fan Passes</p><p className="text-2xl font-bold text-[#FBBF24]">{stats?.total_fan_passes || 0}</p></div>
                <div className="bg-[#141414] border border-white/10 p-4"><p className="text-gray-400 text-xs mb-1">Active Sessions</p><p className="text-2xl font-bold text-green-500">{stats?.active_sessions || 0}</p></div>
                <div className="bg-[#141414] border border-white/10 p-4"><p className="text-gray-400 text-xs mb-1">Forum Topics</p><p className="text-2xl font-bold text-blue-400">{stats?.total_forum_topics || 0}</p></div>
                <div className="bg-[#141414] border border-white/10 p-4"><p className="text-gray-400 text-xs mb-1">Total Comments</p><p className="text-2xl font-bold text-purple-400">{stats?.total_forum_comments || 0}</p></div>
              </div>
              <div className="bg-[#141414] border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-gray-400">Display Name</TableHead>
                      <TableHead className="text-gray-400">Email</TableHead>
                      <TableHead className="text-gray-400">Club</TableHead>
                      <TableHead className="text-gray-400">Country</TableHead>
                      <TableHead className="text-gray-400">Role</TableHead>
                      <TableHead className="text-gray-400">Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forumUsers.users.map((u) => (
                      <TableRow key={u.user_id} className="border-white/10">
                        <TableCell className="text-white font-medium">{u.name || "—"}</TableCell>
                        <TableCell className="text-gray-400 text-sm">{u.email || "—"}</TableCell>
                        <TableCell className="text-[#FBBF24] text-sm">{u.supported_club || "—"}</TableCell>
                        <TableCell className="text-gray-400 text-sm">{u.country || "—"}</TableCell>
                        <TableCell><span className="px-2 py-0.5 text-xs font-bold bg-[#FBBF24]/10 text-[#FBBF24]">{u.role}</span></TableCell>
                        <TableCell className="text-gray-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                      </TableRow>
                    ))}
                    {forumUsers.users.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No forum users yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <Pagination page={forumUsers.page} pages={forumUsers.pages} total={forumUsers.total} onPageChange={setPage} />
            </div>
          </TabsContent>

          {/* ====== REGISTRATIONS ====== */}
          <TabsContent value="registrations">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input placeholder="Search by email, phone, or ID..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-black/50 border-white/20 text-white" data-testid="search-registrations-input" />
                </div>
                <button onClick={() => { setPage(1); fetchRegistrations(); }} className="flex items-center gap-2 bg-[#FBBF24] text-black font-bold px-4 py-2 hover:bg-[#F59E0B] transition-colors"><RefreshCw className="w-4 h-4" />Refresh</button>
              </div>
              <div className="bg-[#141414] border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="border-white/10"><TableHead className="text-gray-400">ID</TableHead><TableHead className="text-gray-400">Email</TableHead><TableHead className="text-gray-400">Country</TableHead><TableHead className="text-gray-400">Phone</TableHead><TableHead className="text-gray-400">Verified</TableHead><TableHead className="text-gray-400">Date</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {registrations.registrations.map((reg) => (
                      <TableRow key={reg.respondent_id} className="border-white/10">
                        <TableCell className="text-white font-mono text-xs">{reg.respondent_id}</TableCell>
                        <TableCell className="text-white">{reg.email}</TableCell>
                        <TableCell className="text-gray-400">{reg.country}</TableCell>
                        <TableCell className="text-gray-400">{reg.phone_number || "—"}</TableCell>
                        <TableCell>{reg.email_verified ? <span className="text-green-500 text-xs font-bold">Yes</span> : <span className="text-gray-600 text-xs">No</span>}</TableCell>
                        <TableCell className="text-gray-500 text-sm">{new Date(reg.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                    {registrations.registrations.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No registrations found</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <Pagination page={registrations.page} pages={registrations.pages} total={registrations.total} onPageChange={setPage} />
            </div>
          </TabsContent>

          {/* ====== PRIZE DRAW ====== */}
          <TabsContent value="prize">
            <div className="space-y-6">
              <div className="flex flex-wrap gap-4">
                <button onClick={handleSelectWinners} className="flex items-center gap-2 bg-[#FBBF24] text-black font-bold px-6 py-3 hover:bg-[#F59E0B] transition-colors" data-testid="select-winners-button"><Gift className="w-5 h-5" />Select 100 Winners</button>
                <button onClick={() => handleExport("winners")} className="flex items-center gap-2 bg-white/10 border border-white/20 text-white px-6 py-3 hover:bg-white/20 transition-colors"><Download className="w-5 h-5" />Export Winners</button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#141414] border border-white/10 p-6"><p className="text-gray-400 text-sm mb-1">Total Eligible</p><p className="text-3xl font-bold text-[#FBBF24]">{stats?.total_prize_eligible || 0}</p></div>
                <div className="bg-[#141414] border border-white/10 p-6"><p className="text-gray-400 text-sm mb-1">Winners Selected</p><p className="text-3xl font-bold text-green-500">{stats?.total_winners || 0}</p></div>
              </div>
              <div className="bg-[#141414] border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="border-white/10"><TableHead className="text-gray-400">Entry ID</TableHead><TableHead className="text-gray-400">Email</TableHead><TableHead className="text-gray-400">Phone</TableHead><TableHead className="text-gray-400">Club</TableHead><TableHead className="text-gray-400">Winner</TableHead><TableHead className="text-gray-400">Date</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {prizeEntries.entries.map((e) => (
                      <TableRow key={e.entry_id} className="border-white/10">
                        <TableCell className="text-white font-mono text-xs">{e.entry_id}</TableCell>
                        <TableCell className="text-white">{e.email}</TableCell>
                        <TableCell className="text-gray-400">{e.phone}</TableCell>
                        <TableCell className="text-[#FBBF24] text-sm">{e.club || "—"}</TableCell>
                        <TableCell>{e.is_winner ? <span className="px-2 py-1 text-xs font-bold bg-green-500/20 text-green-500">WINNER</span> : <span className="text-gray-500">—</span>}</TableCell>
                        <TableCell className="text-gray-500 text-sm">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                    {prizeEntries.entries.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No prize entries</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <Pagination page={prizeEntries.page} pages={prizeEntries.pages} total={prizeEntries.total} onPageChange={setPage} />
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subtext, color }) {
  const c = { blue: "bg-blue-500/10 text-blue-500", purple: "bg-purple-500/10 text-purple-500", green: "bg-green-500/10 text-green-500", yellow: "bg-[#FBBF24]/10 text-[#FBBF24]" };
  return (
    <div className="bg-[#141414] border border-white/10 p-4">
      <div className="flex items-center gap-2 mb-2"><div className={`w-8 h-8 flex items-center justify-center ${c[color]}`}><Icon className="w-4 h-4" /></div><span className="text-gray-400 text-xs">{label}</span></div>
      <p className="text-2xl font-bold text-white">{typeof value === "number" ? value.toLocaleString() : value}</p>
      {subtext && <p className="text-gray-500 text-xs mt-1">{subtext}</p>}
    </div>
  );
}

function BarChartCard({ title, icon: Icon, data, labelKey, color }) {
  if (!data || data.length === 0) return null;
  const max = data[0]?.count || 1;
  return (
    <div className="bg-[#141414] border border-white/10 p-6">
      <h3 className="text-white font-bold mb-4 flex items-center gap-2">{Icon && <Icon className="w-5 h-5" style={{ color }} />}{title}</h3>
      <div className="space-y-3">
        {data.map((d, i) => (
          <div key={d[labelKey] || i}>
            <div className="flex items-center justify-between mb-1"><span className="text-white text-sm truncate mr-2">{d[labelKey]}</span><span className="font-bold text-sm" style={{ color }}>{d.count}</span></div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${(d.count / max) * 100}%`, backgroundColor: color }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PieChartCard({ title, data, labelKey }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.count, 0);
  const colors = ["bg-green-500", "bg-yellow-500", "bg-red-500", "bg-blue-500", "bg-purple-500"];
  return (
    <div className="bg-[#141414] border border-white/10 p-6">
      <h3 className="text-white font-bold mb-4">{title}</h3>
      <div className="space-y-4">
        {data.map((d, i) => {
          const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
          return (
            <div key={d[labelKey]}>
              <div className="flex items-center justify-between mb-1"><span className="text-white text-sm">{d[labelKey]}</span><span className="text-gray-400 text-sm">{pct}% ({d.count})</span></div>
              <div className="h-3 bg-white/5 rounded-full overflow-hidden"><div className={`h-full ${colors[i % colors.length]} rounded-full transition-all`} style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Badge({ value }) {
  if (!value) return <span className="text-gray-600 text-xs">—</span>;
  const c = value === "Yes" ? "bg-green-500/20 text-green-500" : value === "No" ? "bg-red-500/20 text-red-500" : "bg-yellow-500/20 text-yellow-500";
  return <span className={`px-2 py-0.5 text-xs font-bold ${c}`}>{value}</span>;
}

function Pagination({ page, pages, total, onPageChange }) {
  if (total === 0) return null;
  return (
    <div className="flex items-center justify-between">
      <p className="text-gray-500 text-sm">Page {page} of {pages} ({total} total)</p>
      <div className="flex items-center gap-2">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="p-2 text-gray-400 hover:text-white disabled:opacity-30"><ChevronLeft className="w-5 h-5" /></button>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= pages} className="p-2 text-gray-400 hover:text-white disabled:opacity-30"><ChevronRight className="w-5 h-5" /></button>
      </div>
    </div>
  );
}
